import { z } from "zod";

export const StepIdSchema = z.enum(["basic", "about", "treat", "owner"]);

const MealSelectionItemSchema = z.object({
  variantId: z.string(),
  count: z.number(),
});

export const DogFormPersistSchema = z.object({
  quizId: z.string().optional(),
  name: z.string().optional(),
  breed: z.string().optional(),

  sex: z.string().optional(),
  years: z.union([z.number(), z.string()]).optional(),
  months: z.union([z.number(), z.string()]).optional(),
  weightKg: z.union([z.number(), z.string()]).optional(),

  bodyCondition: z.string().optional(),
  reproduction: z.string().optional(),

  allergies: z.array(z.string()).optional(),
  healthIssues: z.array(z.string()).optional(),
  healthIssuesOther: z.string().optional(),

  // Issue #52 question cluster (end of MY DOG, before lead capture)
  currentFood: z.array(z.string()).optional(),
  currentFoodBrand: z.string().optional(),
  triedFresh: z.string().optional(),
  fussiness: z.string().optional(),
  feedingGoal: z.string().optional(),

  activityLevel: z.string().optional(),

  treatFrequency: z.string().optional(),
  dailyCalories: z.union([z.number(), z.string()]).optional(),
  dailyGrams: z.union([z.number(), z.string()]).optional(),

  // 🔹 NEW: owner fields
  ownerName: z.string().optional(),
  ownerEmail: z.string().optional(),
  ownerPhone: z.string().optional(),

  // Plan step
  selectedPackSize: z.union([z.number(), z.string()]).optional(),
  selectedPlanDays: z.union([z.number(), z.string()]).optional(),

  // Per-dog meal selection (array of { variantId, count })
  mealSelection: z.array(MealSelectionItemSchema).optional(),
  mealMode: z.enum(["FULL", "MIXED"]).optional(),
  dailyKey: z.union([z.number(), z.string()]).optional(),

  mealSelectedPacks: z.number().min(0).optional(),
  mealRecommendedPacks: z.number().min(0).optional(),
  mealAutoConfigSig: z.string().optional(),
  marketingOptIn: z.boolean().optional(),

  /**
   * Issue #50: switched out of the current box (answers kept, not charged or
   * shipped). Absent/false = included, so pre-existing saves need no backfill.
   * NOTE: zod strips unknown keys on parse — any new persisted field MUST be
   * added here or it is silently dropped on every hydrate.
   */
  excludedFromBox: z.boolean().optional(),
});

export const PersistSchemaV3 = z.object({
  version: z.literal(2),

  dogOrder: z.array(z.string()),
  dogForms: z.record(z.string(), DogFormPersistSchema),

  activeDogId: z.string().nullable(),

  stepState: z.object({
    perDogStep: z.record(z.string(), StepIdSchema),
    globalStep: StepIdSchema.or(z.literal("results")),
  }),

  resultsStep: z.number(),

  /** Set once the user confirms "How many dogs do you have?" (Figma screen 1.0). */
  dogsCountConfirmed: z.boolean().optional(),

  treatSelections: z
    .record(z.string(), z.record(z.string(), z.number()))
    .optional(),

  deliveryDate: z.string().optional(),
  shipDate: z.string().optional(),
});

export type PersistedStateV3 = z.infer<typeof PersistSchemaV3>;
